// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PaymentTerm } from '@/payment-term/api/types';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
} from '@mui/material';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Determines if the option is equal to the value.
 *
 * @param option - A payment term option to compare.
 * @param value - A payment term value to compare.
 */
function isOptionEqualToValue(option: PaymentTerm, value: PaymentTerm) {
  return option.id === value.id;
}

/**
 * Returns the label of the option.
 *
 * @param option - A payment term option for which to get the label.
 */
function getOptionLabel(option: PaymentTerm) {
  return option.label;
}

interface Props
  extends Omit<
    AutocompleteProps<PaymentTerm, false, false, false>,
    'renderInput' | 'isOptionEqualToValue' | 'getOptionLabel'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;
}

/**
 * Renders a payment term autocomplete component and forwards a ref to the input
 * element.
 */
function PaymentTermAutocompleteRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { name, label, placeholder, error, helperText, ...autocompleteProps } =
    props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * An autocomplete component for selecting a payment term from a list of
 * options.
 */
export const PaymentTermAutocomplete = forwardRef(
  PaymentTermAutocompleteRenderer
);
